import React from "react";
import { Provider, VisuallyHidden } from "react-aria-components";

import {
    CmsGatingForm,
    Context,
    ServerCmsConstantsBreadcrumb,
    ServerCmsConstantsFaqItem,
    ServerCmsModelsSettingsFooterFooterPreviewTemplate,
    ServerCmsModelsSettingsHeaderHeaderPreviewTemplate,
    ThelabuiTypesAlternatePage,
    ThelabuiTypesLanguage,
    ThemeChooserBlock,
} from "@reactivated";

import { MetaTagsBlock } from "@thelabnyc/thelabui/src/components/MetaTagsBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Breadcrumbs } from "../Breadcrumbs";
import { SoftPageGate } from "../SoftPageGate";
import { getThemeClass } from "../ThemedModule";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { clickTrackingValues } from "./clickTrackingValues";

import styles from "./index.module.scss";

type HeaderContent =
    ServerCmsModelsSettingsHeaderHeaderPreviewTemplate["model"];
type FooterContent =
    ServerCmsModelsSettingsFooterFooterPreviewTemplate["model"];

// Replace </ with <\/ so a </script> inside a JSON string value
// can't break out of the script tag.
const JsonLdScript = ({ json }: { json: string }) => (
    <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: json.replace(/<\//g, "<\\/") }}
    />
);

interface Props {
    title: string;
    seo_title: string;
    search_description: string;
    canonical_url: string;
    og_image_url?: string;
    og_type?: string;
    og_article_published_time?: string;
    og_article_modified_time?: string;
    og_article_author?: string;
    og_article_section?: string;
    language: ThelabuiTypesLanguage | null;
    alternate_pages?: ThelabuiTypesAlternatePage[];
    color_theme: string;
    show_breadcrumbs: boolean;
    // breadcrumbs + faq_items are optional at the type level because the
    // Header / Footer / Gating Form / Reusable Content preview templates spread
    // `undefined` into PageSkeleton and never pass either prop. Real page
    // templates always pass an array.
    breadcrumbs?: ServerCmsConstantsBreadcrumb[];
    breadcrumbs_theme: string;
    faq_items?: ServerCmsConstantsFaqItem[];

    gating_form?: {
        fields: CmsGatingForm;
        id: number;
    } | null;
    schema_json?: string;
    robots?: string | null;
    rootClassName?: string;
    headerOverride?: HeaderContent;
    footerOverride?: FooterContent;
    isPreview: boolean;
    /**
     * Set by templates that already render the page title as a visible <h1>
     * (article and event heroes), so PageSkeleton skips its hidden one and the
     * page still has exactly one <h1>.
     */
    hasVisibleH1?: boolean;
}

// Editors write SEO titles as "<topic> | Lean In". The topic half is the best
// page-topic signal we have (Wagtail admin titles are often labels such as
// "Home Page"), so the hidden <h1> uses it, falling back to the page title.
const SITE_NAME_SUFFIX = /\s*\|\s*Lean In\s*$/;
export const pageHeadingText = (seoTitle: string, title: string) =>
    seoTitle.replace(SITE_NAME_SUFFIX, "").trim() || title;

export const PageSkeleton = (props: React.PropsWithChildren<Props>) => {
    const context = React.useContext(Context);
    const headerData = props.headerOverride ?? context.header_content;
    const footerData = props.footerOverride ?? context.footer_content;
    // Skip crumbs with empty urls: an ancestor without a resolvable URL yields
    // url: "" from base.py's breadcrumbs(), and Google flags an empty "item"
    // as invalid — enough to invalidate the whole BreadcrumbList for that page.
    const breadcrumbsForSchema = props.breadcrumbs?.filter((c) => c.url) ?? [];
    const faqItemsForSchema = props.faq_items ?? [];
    // Shared values for OpenGraph + Twitter Card meta tags. Editors set these
    // through the standard Wagtail promote fields (SEO title, meta image,
    // meta description). og:type comes from the page model (BasePage default
    // "website"; ArticlePage overrides to "article"), which unlocks the
    // article:published_time / modified_time / author / section tags LinkedIn
    // and Facebook use for richer article previews.
    const ogTitle = props.seo_title || props.title;
    const ogDescription = props.search_description;
    const ogUrl = props.canonical_url || context.request.url;
    const ogImage = props.og_image_url;
    const ogType = props.og_type || "website";
    const isArticle = ogType === "article";
    // article:* tags only ride along when og:type=article (the backend
    // already returns "" for non-article pages, but the frontend gate stops
    // a template from accidentally spreading article:* on a website page).
    const articleTags: ReadonlyArray<[string, string | undefined]> = isArticle
        ? [
              ["article:published_time", props.og_article_published_time],
              ["article:modified_time", props.og_article_modified_time],
              ["article:author", props.og_article_author],
              ["article:section", props.og_article_section],
          ]
        : [];

    React.useEffect(() => {
        const cookieyes = document.createElement("script");
        cookieyes.id = "cookieeyes";
        cookieyes.type = "text/javascript";
        cookieyes.src =
            "https://cdn-cookieyes.com/client_data/8e92847b55e7418a9f77a4756525d27e/script.js";
        document.body.appendChild(cookieyes);
    });

    return (
        <html lang={props.language?.code}>
            <head>
                <meta charSet="utf-8" />
                <title>{props.seo_title || props.title}</title>
                {props.search_description && (
                    <meta
                        name="description"
                        content={props.search_description}
                    />
                )}
                {props.robots && <meta name="robots" content={props.robots} />}
                {/*
                    OpenGraph + Twitter Card tags so links shared on LinkedIn,
                    X/Twitter, Facebook, Slack, iMessage, and everywhere else
                    render as rich previews (title + description + image)
                    instead of blank text-only cards.
                */}
                <meta property="og:site_name" content="Lean In" />
                <meta property="og:type" content={ogType} />
                <meta property="og:title" content={ogTitle} />
                {ogDescription && (
                    <meta property="og:description" content={ogDescription} />
                )}
                <meta property="og:url" content={ogUrl} />
                {ogImage && <meta property="og:image" content={ogImage} />}
                {articleTags.map(
                    ([property, content]) =>
                        content && (
                            <meta
                                key={property}
                                property={property}
                                content={content}
                            />
                        ),
                )}
                <meta
                    name="twitter:card"
                    content={ogImage ? "summary_large_image" : "summary"}
                />
                <meta name="twitter:title" content={ogTitle} />
                {ogDescription && (
                    <meta name="twitter:description" content={ogDescription} />
                )}
                {ogImage && <meta name="twitter:image" content={ogImage} />}
                {MetaTagsBlock({
                    tags: context.meta_tags.meta_tags.value,
                })}
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <link rel="icon" href={context.core_assets.favicon} />
                <link
                    rel="canonical"
                    href={props.canonical_url || context.request.url}
                />
                {props.alternate_pages?.map((alt) => (
                    <link
                        key={alt.language.code}
                        rel="alternate"
                        hrefLang={alt.language.code}
                        href={alt.url}
                    />
                ))}
                {props.schema_json && <JsonLdScript json={props.schema_json} />}
                {breadcrumbsForSchema.length > 0 && (
                    <JsonLdScript
                        json={JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "BreadcrumbList",
                            "itemListElement": breadcrumbsForSchema.map(
                                (crumb, i) => ({
                                    "@type": "ListItem",
                                    "position": i + 1,
                                    "name": crumb.label,
                                    "item": crumb.url,
                                }),
                            ),
                        })}
                    />
                )}
                {faqItemsForSchema.length > 0 && (
                    <JsonLdScript
                        json={JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "FAQPage",
                            "mainEntity": faqItemsForSchema.map((item) => ({
                                "@type": "Question",
                                "name": item.question,
                                "acceptedAnswer": {
                                    "@type": "Answer",
                                    "text": item.answer_html,
                                },
                            })),
                        })}
                    />
                )}
            </head>
            <body data-commit={context.CI_COMMIT_SHA}>
                <VisuallyHidden isFocusable>
                    <a href="#maincontent">Skip to content</a>
                </VisuallyHidden>
                {headerData && <Header value={headerData.body} />}
                <main
                    id="maincontent"
                    className={concatClassNames([
                        styles.container,
                        props.color_theme !== "unthemed"
                            ? getThemeClass(
                                  props.color_theme as ThemeChooserBlock,
                              )
                            : undefined,
                        props.rootClassName,
                    ])}
                >
                    {/*
                        Every page emits exactly one <h1> here so that page
                        topic semantics are guaranteed regardless of which
                        blocks the editor arranges in the body. It's visually
                        hidden because the visible page headline (usually a
                        hero) is styled by design and lives inside the body.
                        Body blocks wrap themselves in <HLevel>, so their
                        headings start at h2 under this one.
                    */}
                    {!props.hasVisibleH1 && (
                        <VisuallyHidden>
                            <h1>
                                {pageHeadingText(props.seo_title, props.title)}
                            </h1>
                        </VisuallyHidden>
                    )}
                    {props.show_breadcrumbs && (
                        <Breadcrumbs
                            value={props.breadcrumbs ?? []}
                            theme={props.breadcrumbs_theme as ThemeChooserBlock}
                        />
                    )}
                    <Provider values={clickTrackingValues("cta")}>
                        {props.children}
                    </Provider>
                </main>
                {footerData && (
                    <Footer value={footerData} socials={context.socials} />
                )}
                {props.gating_form && !props.isPreview && (
                    <SoftPageGate
                        fields={props.gating_form.fields.value}
                        formId={props.gating_form.id}
                    />
                )}
            </body>
        </html>
    );
};
